import { Injectable } from '@angular/core';
import { GetTimestampFromTime } from '@dave/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, EMPTY, firstValueFrom, from, mergeMap, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { AppGatewayService } from '../../services/app-gateway.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { TextTemplateActionTypes } from '../actions/text-template.actions';
import { State } from '../index';
import { TextTemplateEntityFromBackend } from '../../entities/text-template.entity';
import { getTextTemplateDictionary } from '../selectors/text-template.selector';

enum ErrorCodes {
    Add = 'Textvorlage Hinzufügen fehlgeschlagen',
    Load = 'Textvorlagen Abrufen fehlgeschlagen',
    Modify = 'Textvorlage Bearbeiten fehlgeschlagen',
    Remove = 'Textvorlage Löschen fehlgeschlagen',
}

@Injectable()
export class TextTemplateEffects {
    ModifyTextTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateActionTypes.Change),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateEdit: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateActionTypes.UpdateOne({ Payload: TextTemplateEntityFromBackend(res.BaseTextTemplateEdit.TextTemplate) });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    DeleteTextTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateActionTypes.Delete),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateDelete: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateActionTypes.RemoveOne({ Payload: +Payload.Id });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Remove,
                                Err: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    AddTextTemplate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateActionTypes.CreateRequest),
            concatMap(({ Payload }) =>
                this.gatewayService
                    .Request({ BaseTextTemplateAdd: Payload })
                    .then((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            return TextTemplateActionTypes.CreateSuccess({
                                Payload: TextTemplateEntityFromBackend(res.BaseTextTemplateAdd.TextTemplate),
                            });
                        } else {
                            throw res.Errors;
                        }
                    })
                    .catch((err) =>
                        TextTemplateActionTypes.CreateFailure({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Errors: err,
                            },
                        }),
                    ),
            ),
        ),
    );

    LoadTextTemplates = createEffect(() => {
        return this.actions$.pipe(
            ofType(TextTemplateActionTypes.Load),
            switchMap(({ Payload }) => {
                const requestPayload = isNotNullOrUndefined(Payload?.updatedSince) ? { UpdatedSince: Payload.updatedSince, TagIds: [] } : { TagIds: [] };

                return from(this.gatewayService.Request({ BaseTextTemplateGet: requestPayload })).pipe(
                    mergeMap((res) => {
                        if (Object.keys(res?.Errors || {}).length === 0) {
                            if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                return of(
                                    TextTemplateActionTypes.UpdateAll({
                                        Payload: res.BaseTextTemplateGet.TextTemplates.map((val) => TextTemplateEntityFromBackend(val)),
                                        updateLatestUpdatedAt: true,
                                    }),
                                );
                            } else if (res.BaseTextTemplateGet.TextTemplates.length) {
                                return from(firstValueFrom(this.store$.select(getTextTemplateDictionary))).pipe(
                                    mergeMap((TextTemplates) => {
                                        const filteredTextTemplates = res.BaseTextTemplateGet.TextTemplates.filter((TextTemplate) => {
                                            const fromState = TextTemplates[TextTemplate.Id];
                                            return !fromState || GetTimestampFromTime(fromState.UpdatedAt).toString() !== TextTemplate.UpdatedAt;
                                        });

                                        if (filteredTextTemplates.length) {
                                            return of(
                                                TextTemplateActionTypes.UpdateMany({
                                                    Payload: filteredTextTemplates.map((val) => TextTemplateEntityFromBackend(val)),
                                                    updateLatestUpdatedAt: true,
                                                }),
                                            );
                                        } else {
                                            return EMPTY;
                                        }
                                    }),
                                );
                            }
                            return EMPTY;
                        } else {
                            throw res.Errors;
                        }
                    }),
                    catchError((err) =>
                        of(
                            BaseActionTypes.ErrorAction({
                                Payload: {
                                    ToasterMessage: ErrorCodes.Load,
                                    Err: err,
                                },
                            }),
                        ),
                    ),
                );
            }),
        );
    });
    error$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TextTemplateActionTypes.CreateFailure),
            map(({ Payload }) =>
                BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: Payload.ToasterMessage,
                        Err: Payload.Errors,
                    },
                }),
            ),
        ),
    );
    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayService: AppGatewayService) {}
}
